import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { Plus, FileText, Search, ExternalLink, Calendar, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function MBDashboard() {
  const navigate = useNavigate();
  const [mbList, setMbList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMBs();
  }, []);

  async function fetchMBs() {
    const { data, error } = await supabase
      .from("measurement_bills")
      .select(`*, sites(site_name)`)
      .order("created_at", { ascending: false });
    if (data) setMbList(data);
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-end mb-8">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">MEASUREMENT BOOKS</h1>
            <p className="text-slate-500 font-medium">Manage and track site project measurements</p>
          </div>
          <button
            onClick={() => navigate("/mb/create")}
            className="bg-[#0054A6] hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-blue-200 transition-all active:scale-95"
          >
            <Plus size={20} /> CREATE NEW MB
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
            <p className="text-slate-400 text-xs font-black uppercase tracking-widest mb-1">Total Records</p>
            <p className="text-2xl font-black text-slate-900">{mbList.length}</p>
          </div>
        </div>

        {/* List Table */}
        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-8 py-5">Date / Bill No</th>
                <th className="px-6 py-5">Site / PO Number</th>
                <th className="px-6 py-5 text-right">Total Amount</th>
                <th className="px-8 py-5 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {mbList.map((mb) => (
                <tr key={mb.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-8 py-5">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900">{mb.bill_no}</span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar size={12} /> {mb.date}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-700 flex items-center gap-1">
                        <MapPin size={12} className="text-blue-500" /> {mb.sites?.site_name}
                      </span>
                      <span className="text-[10px] font-black text-slate-400 uppercase">PO: {mb.po_number || "N/A"}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <span className="font-black text-[#0054A6]">₹{mb.total_amount?.toLocaleString("en-IN")}</span>
                  </td>
                  <td className="px-8 py-5 text-center">
                    <button className="text-slate-400 hover:text-blue-600 transition-colors">
                      <ExternalLink size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {mbList.length === 0 && !loading && (
            <div className="p-20 text-center text-slate-400 font-medium">No records found. Click "Create New MB" to start.</div>
          )}
        </div>
      </div>
    </div>
  );
}